/**
 * feedScheduled.js — who sees a scheduled post before it publishes (RW-2986).
 *
 * The composer writes a scheduled post straight into `posts` with status
 * 'scheduled' and a fresh serverTimestamp (Feed.js handleCreatePost →
 * postPayload.js), and publishScheduledPosts flips it to 'published' at its
 * time. Every Home read (App.js's live window, older pages, FeedLayout's rail)
 * is the plain tenant + office query, so the pending post arrives with them —
 * and, carrying the newest timestamp, sat at the top of everyone's stream.
 *
 * Filtered on the client, not in the query: `where('status', …)` needs a new
 * composite index and would drop every legacy post that has no `status`.
 * A post with no status is published (isPublishedPost); only 'scheduled' waits.
 *
 * The author still sees their own pending post, so a scheduled post does not
 * look lost. Admins manage the queue in Admin → Scheduled Posts.
 *
 * Pure: no Firestore, no React.
 */
import { isPublishedPost } from './feedHashtag';
import { isStickyPost } from './feedSticky';

export { isPublishedPost };

/** May `viewerUid` see this post on Home right now? */
export function isFeedVisiblePost(post, viewerUid) {
  if (!post) return false;
  if (isPublishedPost(post)) return true;
  return !!viewerUid && post.authorUid === viewerUid;
}

/**
 * RW-3877: may `viewerUid` see this post wherever posts are listed — Home,
 * Connect search, global search, a profile's posts, Recognition? Not
 * soft-deleted (feedPostRepository softDeletePost sets `deleted: true`) and
 * visible on Home (isFeedVisiblePost). This is the feed's own filter, shared so
 * no other surface lists a post the feed hides.
 */
export function isShownPost(post, viewerUid) {
  return !!post && post.deleted !== true && isFeedVisiblePost(post, viewerUid);
}

/**
 * The sidebar's "Requires your attention" badge (App.js): the posts Feed.js
 * pins for `uid` — not deleted, visible on Home, sticky. One rule, so the badge
 * and the screen it opens cannot disagree.
 */
export function countAttentionPosts(posts, uid, now = Date.now()) {
  if (!uid || !Array.isArray(posts)) return 0;
  return posts.filter((p) => isShownPost(p, uid) && isStickyPost(p, uid, now)).length;
}

/** True when the post is still waiting for publishScheduledPosts. */
export function isPendingScheduledPost(post) {
  return !!post && post.status === 'scheduled';
}

/** scheduledFor as a Date — Firestore Timestamp, {seconds}, Date, millis or ISO — or null. */
export function scheduledForDate(value) {
  if (value == null || value === '') return null;
  let d = null;
  if (typeof value.toDate === 'function') d = value.toDate();
  else if (value instanceof Date) d = value;
  else if (typeof value === 'number' || typeof value === 'string') d = new Date(value);
  else if (typeof value.seconds === 'number') d = new Date(value.seconds * 1000);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
}

/**
 * The author's "Scheduled" chip on PostCard: { label, title }.
 * "Scheduled for Fri, Oct 9 · 9:00 AM" (viewer's local time); "Scheduled"
 * when the date is missing or unreadable. `title` carries the full date.
 */
export function scheduledChipText(scheduledFor) {
  const d = scheduledForDate(scheduledFor);
  if (!d) return { label: 'Scheduled', title: 'Scheduled — publishes automatically' };
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const full = d.toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  return { label: `Scheduled for ${day} · ${time}`, title: `Publishes ${full}` };
}
